import { BlendFunction, Effect, EffectAttribute } from "postprocessing";
import { Uniform, Matrix4 } from "three";

const fragmentShader = `
uniform mat4 projectionMatrixInverse;
uniform mat4 viewMatrixInverse;
uniform float fog_intensity;


// Function to reconstruct world position
vec3 getWorldPositionFromDepth(vec2 uv, float depth) {
    // Convert depth to NDC
    float z = depth * 2.0 - 1.0;

    // Convert UV to NDC
    vec2 ndc = uv * 2.0 - 1.0;

    // Create clip space position
    vec4 clipPos = vec4(ndc, z, 1.0);

    // Transform to view space
    vec4 viewPos = projectionMatrixInverse * clipPos;
    viewPos /= viewPos.w;

    // Transform to world space
    vec4 worldPos = viewMatrixInverse * viewPos;
    return worldPos.xyz;
}

void mainImage(const in vec4 inputColor, const in vec2 uv, const in float depth, out vec4 outputColor) {
    vec4 original = inputColor;
    float viewZ = getViewZ(depth);

    float fogStart = 0.0;
    float fogEnd = 200.0;
    float distance = -viewZ;

    float fogFactorLinear = clamp((distance - fogStart) / (fogEnd - fogStart), 0.0, 1.0);
    float fogFactor = pow(fogFactorLinear, 2.0);

    // Get world position
    vec3 worldPos = getWorldPositionFromDepth(uv, depth);

    // Height-based fog mask
    float worldHeightMin = 0.0;   // World Y where fog is strongest
    float worldHeightMax = 250.0;  // World Y where fog fades out

    // Calculate height mask (1 = full fog at bottom, 0 = no fog at top)
    float heightMaskLinear = 1.0 - clamp((worldPos.y - worldHeightMin) / (worldHeightMax - worldHeightMin), 0.0, 1.0);
    float heightMask = pow(heightMaskLinear, 2.0);

    // Combine distance and height fog
    float finalFogFactor = fogFactor * heightMask;

    vec4 fogColor = vec4(0.95, 0.9, 0.8, 0.5); // Warm golden-white

    
    outputColor = mix(original, fogColor, finalFogFactor); // Added 0.5 to reduce intensity
}
`;

/*

//     // Alternative: use linear interpolation instead of smoothstep
//     float fogFactorLinear = clamp((worldDistance - fogStart) / (fogEnd - fogStart), 0.0, 1.0);

//     // Apply power curve for smooth falloff
//     float fogFactor = pow(fogFactorLinear, fogPower);

//     // Mix original color with fog color based on fog factor
//     // fogFactor = 0.0 -> show original color (no fog)
//     // fogFactor = 1.0 -> show fog color (full fog)
//     vec3 mixedColor = mix(inputColor.rgb, fogColor.a, fogFactor * fogIntensity);
*/





/*
const fragmentShader = `
//uniform sampler2D gradient; // The fog gradient texture
uniform float fog_intensity;
uniform float fog_amount;
uniform mat4 cameraInverseProjectionMatrix; // Inverse projection matrix of the camera
uniform sampler2D heightFogRamp; // Your color ramp texture

void mainImage(const in vec4 inputColor, const in vec2 uv, const in float depth, out vec4 outputColor) {
    
    vec4 original = inputColor;
    float viewZ = getViewZ(depth);

    float fogStart = 10.0;
    float fogEnd = 100.0;
    float distance = -viewZ;

    float fogFactor = clamp((distance - fogStart) / (fogEnd - fogStart), 0.0, 1.0);

    // Mask based on screen Y coordinate (0 = bottom, 1 = top)
    float screenY = uv.y;
    float heightMask = 1.0 - screenY; // 1 at bottom, 0 at top

    // Apply smooth falloff
    heightMask = smoothstep(0.0, 0.7, heightMask); // Fog starts fading at 70% from bottom

    float finalFogFactor = fogFactor * heightMask;

    vec4 fogColor = vec4(1.0, 0.0, 0.0, 1.0);
    outputColor = mix(original, fogColor, finalFogFactor * fog_intensity);




    // vec4 original = inputColor;
    // float viewZ = getViewZ(depth);

    // float fogStart = 10.0;  // Distance where fog starts
    // float fogEnd = 100.0;   // Distance where fog is maximum
    // float distance = -viewZ; // Make viewZ positive

    // float fogFactor = clamp((distance - fogStart) / (fogEnd - fogStart), 0.0, 1.0);
    // vec4 fogColor = vec4(1.0, 0.0, 0.0, 1.0);
    // outputColor = mix(original, fogColor, fogFactor * fog_intensity);







    // vec4 original = inputColor;
    // float viewZ = getViewZ(depth);

    // float distance = -viewZ;
    // float fogFactor = 1.0 - exp(-distance * fog_amount);
    // fogFactor = clamp(fogFactor, 0.0, 1.0);

    // vec4 fogColor = vec4(1.0, 0.0, 0.0, 1.0);
    // outputColor = mix(original, fogColor, fogFactor * fog_intensity);
}
`;
*/
export class FogEffect extends Effect {
    constructor(camera, fogIntensity) {
        super("FogEffect", fragmentShader, {
            blendFunction: BlendFunction.NORMAL,
            attributes: EffectAttribute.DEPTH, // Request depth buffer access
            uniforms: new Map([
                ['fog_intensity', new Uniform(fogIntensity)],
                ["projectionMatrixInverse", new Uniform(camera.projectionMatrixInverse)],
                ["viewMatrixInverse", new Uniform(camera.matrixWorld)],
                ["cameraNear", new Uniform(camera.near)],
                ["cameraFar", new Uniform(camera.far)],
            ])
        });

        this.camera = camera;

    }
}


/*
import { BlendFunction, Effect, EffectAttribute } from "postprocessing";
import { Uniform } from "three";



const fragmentShader = `
uniform float cameraNear;
uniform float cameraFar;
uniform float fogStart;
uniform float fogEnd;
uniform float fogIntensity;
uniform mat4 cameraInverseProjectionMatrix;
uniform mat4 cameraMatrixWorld;

float readDepth(sampler2D depthSampler, vec2 coord) {
    float fragCoordZ = texture2D(depthSampler, coord).x; //raw depth value (0.0 - 1.0) stored in depth buffer NDC nomalized device coord. 
    float viewZ = perspectiveDepthToViewZ(fragCoordZ, cameraNear, cameraFar); //transform to view space -> actual -z value to fragment
    //return viewZ;
    return viewZToOrthographicDepth(viewZ, cameraNear, cameraFar);
}

vec3 getWorldPositionFromDepth(vec2 uv, float depth) {
    // Convert UV to NDC coordinates
    vec2 ndc = uv * 2.0 - 1.0;

    // Create NDC position with depth
    vec4 ndcPos = vec4(ndc, depth * 2.0 - 1.0, 1.0);

    // Transform to view space
    vec4 viewPos = cameraInverseProjectionMatrix * ndcPos;
    viewPos /= viewPos.w;

    // Transform to world space
    vec4 worldPos = cameraMatrixWorld * viewPos;

    return worldPos.xyz;
}

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {

    float fogEnd = 500.0;
    float fogStart = 0.0;
    float fogPower = 0.9;
    float fogIntensity = 0.75;
    vec3 fogColor = vec3(1.0, 1.0, 1.0);

    // Height fog parameters
    float fogHeightStart = 0.0;   // Height where fog starts
    float fogHeightEnd = 20.0;    // Height where fog is fully applied
    float fogHeightFalloff = 1.5; // Controls how sharply fog cuts off with height

    // Read linearized depth value
    float depth = readDepth(depthBuffer, uv);

    // Convert normalized depth back to world space distance
    float worldDistance = mix(cameraNear, cameraFar, depth);

    // Reconstruct world position from depth and UV
    vec3 worldPosition = getWorldPositionFromDepth(uv, depth);

    // Height-based fog factor (0.0 = no fog above fogHeightEnd, 1.0 = full fog below fogHeightStart)
    float heightFactor = 1.0 - clamp((-worldPosition.y - fogHeightStart) / (fogHeightEnd - fogHeightStart), 0.0, 1.0);
    heightFactor = pow(heightFactor, fogHeightFalloff);

    // Distance-based fog factor
    float fogFactorLinear = clamp((worldDistance - fogStart) / (fogEnd - fogStart), 0.0, 1.0);
    float distanceFogFactor = pow(fogFactorLinear, fogPower);

    // Combine height and distance fog factors
    float finalFogFactor = distanceFogFactor * heightFactor;

    // Mix original color with fog color
    vec3 mixedColor = mix(inputColor.rgb, fogColor, finalFogFactor * fogIntensity);
    //outputColor = vec4(mixedColor, 1.0);
    outputColor = vec4(vec3(worldPosition.y * 0.01), 1.0);
}
`;


export class FogEffect extends Effect {
    constructor(camera, fogIntensity) {
        super("DepthVisualizationEffect", fragmentShader, {
            blendFunction: BlendFunction.NORMAL,
            attributes: EffectAttribute.DEPTH, // Request depth buffer access
            uniforms: new Map([
                ["cameraNear", new Uniform(camera.near)],
                ["cameraFar", new Uniform(camera.far)],
                ['fogIntensity', new Uniform(fogIntensity)],
                ["cameraInverseProjectionMatrix", new Uniform(camera.projectionMatrixInverse)],
                ["cameraMatrixWorld", new Uniform(camera.matrixWorld)],
            ])
        });

        this.camera = camera;

    }
}
    */